import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { HslaColor, HslaColorPicker, HslColor, HslColorPicker } from 'react-colorful';
import { token } from '@bynder/design-system';
import { InputContainer } from './InputContainer';
import { ColorHexInput } from './ColorHexInput';
import { NumberInputInlineSuffix } from '../NumberInputInlineSuffix';
import { hslaToHex } from '../../utils/hsl';
import { useThrottle } from 'packages/hooks/useThrottle';

type Props = {
    defaultValue: HslColor | HslaColor;
    onChange: (color: HslaColor) => void;
    isColorDragging?: boolean;
};

export type HueAndOpacityRef = {
    setSaturationLightness: (s: number, l: number) => void;
    setValue: (value: HslColor | HslaColor) => void;
};

export const HueAndOpacity = forwardRef<HueAndOpacityRef, Props>(function HueAndOpacity(props, ref) {
    const [color, setColor] = useState<HslColor | HslaColor>(props.defaultValue);
    const [opacity, setOpacity] = useState<number>('a' in props.defaultValue ? props.defaultValue.a * 100 : 100);
    const [hexValue, setHexValue] = useState(() => toHex(color));
    const tmpValue = useRef<HslaColor>({ a: 1, ...color });

    useImperativeHandle(
        ref,
        () => ({
            setSaturationLightness(s: number, l: number) {
                tmpValue.current = { ...tmpValue.current, s, l };

                setColor(tmpValue.current);
                setColor(tmpValue.current);
                setHexValue(toHex(tmpValue.current));
            },
            setValue(value: HslColor | HslaColor) {
                tmpValue.current = { ...tmpValue.current, ...value };

                setColor(tmpValue.current);
                setOpacity(tmpValue.current.a * 100);
                setHexValue(toHex(tmpValue.current));
            },
        }),
        [],
    );

    function onHexChange(value: string) {
        tmpValue.current = tinycolor(value).setAlpha(tmpValue.current.a).toHsl();
        tmpValue.current.s *= 100;
        tmpValue.current.l *= 100;

        setColor(tmpValue.current);
        setHexValue(toHex(tmpValue.current));

        props.onChange(tmpValue.current);
    }

    function onOpacityChange(value: number) {
        tmpValue.current = { ...tmpValue.current, a: value / 100 };

        setColor(tmpValue.current);
        setOpacity(value);

        props.onChange(tmpValue.current);
    }

    const updateHue = useCallback(
        (value: HslColor | HslaColor) => {
            tmpValue.current = { ...tmpValue.current, h: value.h };

            if ('a' in value) {
                tmpValue.current = { ...tmpValue.current, a: value.a };
                setOpacity(value.a * 100);
            }

            setHexValue(toHex(tmpValue.current));

            props.onChange(tmpValue.current);
        },
        [props.onChange],
    );

    const onHueChangeThrottled = useThrottle(updateHue, 25);

    useEffect(() => {
        if (!props.isColorDragging) {
            setColor(props.defaultValue);
            setOpacity('a' in props.defaultValue ? props.defaultValue.a * 100 : 100);
            setHexValue(toHex(props.defaultValue));
            tmpValue.current = { a: 1, ...props.defaultValue };
        }
    }, [props.defaultValue]);

    if (!('a' in props.defaultValue)) {
        return (
            <>
                <HuePicker color={color} onChange={onHueChangeThrottled} />
                <InputContainer>
                    <ColorHexInput value={hexValue} onChange={onHexChange} />
                </InputContainer>
            </>
        );
    }

    return (
        <>
            <HuePicker as={HslaColorPicker} color={color as HslaColor} onChange={onHueChangeThrottled} />
            <InputContainer $hasAlphaChannel>
                <ColorHexInput value={hexValue} opacity={opacity} onChange={onHexChange} />
                <NumberInputInlineSuffix value={opacity} onChange={onOpacityChange} suffix="%" min={0} max={100} />
            </InputContainer>
        </>
    );
});

const HuePicker = styled(HslColorPicker)`
    .react-colorful__saturation {
        display: none;
    }

    .react-colorful__hue,
    .react-colorful__alpha {
        height: 8px;
        margin: 0 ${token.spacing5} ${token.spacing6};
        border-radius: ${token.radiusPill};
    }

    .react-colorful__hue {
        box-shadow: inset 0 0 0 1px ${token.gray100a};
    }

    .react-colorful__alpha {
        background-image: url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect height='8' width='8' y='0' x='0' fill='%23ffffff'/%3E%3Crect height='4' width='4' y='0' x='0' fill='%23e9ecee'/%3E%3Crect height='4' width='4' y='4' x='4' fill='%23e9ecee'/%3E%3C/svg%3E%0A");
    }

    .react-colorful__alpha-gradient {
        height: 8px;
        border-radius: ${token.radiusBase};
        box-shadow: inset 0 0 0 1px ${token.gray100a};
    }
`;

function toHex(color: HslColor | HslaColor) {
    return hslaToHex(color.h, color.s, color.l);
}
